import { useState } from "react";
import {
  ArrowUpRight,
  Plus,
  Controller,
  Diagram3,
  Cpu,
  SuitSpade,
} from "react-bootstrap-icons";

const experiences = [
  {
    company: "Seeq",
    period: "Feb 2026 — Present",
    role: "Staff Software Developer",
    current: true,
    description:
      "Working across the full stack on Insightboard and Sqittle with Kotlin, React, and Python. Using Claude Code, Codex, and Cursor in everyday development, and building AI agents and reusable agent skills.",
    tags: [
      "Kotlin",
      "React",
      "Python",
      "Full-stack development",
      "AI agents",
      "Agent skills",
    ],
  },
  {
    company: "Mach7 Technologies",
    period: "2023 — 2025",
    role: "Staff Software Engineer",
    description:
      "Led application development for medical imaging and data management, helping healthcare teams work with complex information.",
    tags: ["Medical imaging", "Architecture", "Team leadership"],
    details: [
      "Drove full-stack features from architecture and concept through deployment.",
      "Improved performance across medical imaging workflows.",
      "Helped rebuild the application team through recruitment, onboarding, and mentoring.",
    ],
    links: [["Explore eUnity", "https://www.mach7t.com/eunity2024"]],
  },
  {
    company: "Qlik",
    period: "2021 — 2023",
    role: "Senior Full Stack Engineer",
    description:
      "Built analytics features and data preparation tools for Qlik Sense, connecting enterprise data with actionable insights.",
    tags: ["Analytics", "React & Angular", "Node.js"],
    details: [
      "Led full-stack development from architecture design to implementation.",
      "Built data preparation services with Node.js and improved frontend experiences in React and Angular.",
      "Collaborated across teams on data modeling and business intelligence features.",
    ],
    links: [
      ["Qlik Sense", "https://www.qlik.com/us/products/qlik-sense"],
      ["NLG insights", "https://www.youtube.com/watch?v=dCqoNSBfm2I"],
      [
        "Time series forecasting",
        "https://www.youtube.com/watch?v=qREfZiLZmDc",
      ],
      ["Macro analysis grid", "https://www.youtube.com/watch?v=GE34qF6e3_4"],
    ],
  },
  {
    company: "IBM",
    period: "2012 — 2021",
    role: "Visualization Team Lead & Feature Owner",
    description:
      "Helped build Planning Analytics Workspace from the ground up, leading full-stack development and a team of engineers.",
    tags: ["Data visualization", "Full-stack engineering", "Mentorship"],
    details: [
      "Contributed as an original developer of Planning Analytics Workspace.",
      "Designed reusable frontend and backend frameworks for enterprise-scale solutions.",
      "Built and mentored a team of new graduates and senior engineers while leading feature delivery.",
    ],
    links: [
      [
        "Planning Analytics",
        "https://www.ibm.com/docs/en/planning-analytics/2.0.0",
      ],
      ["Workspace demo", "https://www.youtube.com/watch?v=EGywKNH42qc"],
      ["UI redesign", "https://www.youtube.com/watch?v=kxyPtXknbQQ"],
      ["AI prediction", "https://www.youtube.com/watch?v=02ChRbyYQWg"],
    ],
  },
];
const projects = [
  {
    title: "Game Together",
    category: "Play",
    icon: Controller,
    description:
      "A social gaming platform built around a simple idea: good games bring people together.",
    url: "https://gametogether.ca",
    label: "Visit platform",
    tags: ["Social gaming", "Interactive experiences"],
    featured: true,
  },
  {
    title: "Architecture Diagram Generator",
    category: "Tools",
    icon: Diagram3,
    description:
      "Turn API specifications into live architecture diagrams. A clearer view of how everything connects.",
    url: "https://main.d13j0p1n7g0bna.amplifyapp.com/",
    label: "Explore the tool",
    tags: ["OpenAPI", "Developer tools"],
  },
  {
    title: "AI Sim Workspace",
    category: "AI",
    icon: Cpu,
    description:
      "An interactive space to explore intelligent agent behaviors and machine learning models.",
    url: "https://main.d2nx78bics3fdb.amplifyapp.com/",
    label: "Launch workspace",
    tags: ["AI", "Simulation"],
  },
  {
    title: "Texas Poker Winner Test",
    category: "Play",
    icon: SuitSpade,
    description:
      "An interactive poker hand evaluator, built with a serverless architecture on AWS Amplify.",
    url: "https://master.d1gpp3h7uj6yz6.amplifyapp.com/",
    label: "Try the demo",
    tags: ["AWS Amplify", "Serverless"],
  },
];

export const Projects = () => {
  const [filter, setFilter] = useState("All");
  const visibleProjects = projects.filter(
    (project) => filter === "All" || project.category === filter,
  );
  return (
    <>
      <section
        className="experience-section page-shell"
        id="experiences"
        aria-labelledby="experience-title"
      >
        <div className="section-heading">
          <div>
            <p className="eyebrow">01 / The journey so far</p>
            <h2 id="experience-title">
              Good software.
              <br />
              <em>Built with good people.</em>
            </h2>
          </div>
          <p>
            From enterprise analytics to medical imaging—and now, a new chapter
            at Seeq.
          </p>
        </div>
        <div className="experience-list">
          {experiences.map((experience, index) => (
            <article
              key={experience.company}
              className={`experience-row${experience.current ? " experience-current" : ""}`}
            >
              <div className="experience-date">
                <span className="experience-number">
                  0{experiences.length - index}
                </span>
                <span>{experience.period}</span>
                {experience.current && (
                  <span className="current-badge">
                    <span className="status-dot" /> Current
                  </span>
                )}
              </div>
              <div className="experience-content">
                <h3>{experience.company}</h3>
                <p className="experience-role">{experience.role}</p>
                <p className="experience-description">
                  {experience.description}
                </p>
                <ul className="tag-list">
                  {experience.tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
                {experience.details && (
                  <details className="experience-details">
                    <summary>
                      More about this role <Plus aria-hidden="true" />
                    </summary>
                    <ul>
                      {experience.details.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                    <div className="experience-links">
                      {experience.links.map(([label, url]) => (
                        <a
                          className="text-link"
                          key={url}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label} <ArrowUpRight aria-hidden="true" />
                        </a>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
      <section
        className="projects-section"
        id="projects"
        aria-labelledby="projects-title"
      >
        <div className="page-shell">
          <div className="section-heading">
            <div>
              <p className="eyebrow">02 / Beyond the day job</p>
              <h2 id="projects-title">
                Curiosity, <em>in code.</em>
              </h2>
            </div>
            <p>
              A few things I’ve built to explore an idea, solve a problem, or
              just see what’s possible.
            </p>
          </div>
          <div
            className="project-filter"
            role="group"
            aria-label="Filter projects"
          >
            {["All", "AI", "Tools", "Play"].map((category) => (
              <button
                type="button"
                key={category}
                aria-pressed={filter === category}
                onClick={() => setFilter(category)}
              >
                {category === "All" ? "All projects" : category}
                <span>
                  {category === "All"
                    ? projects.length
                    : projects.filter(
                        (project) => project.category === category,
                      ).length}
                </span>
              </button>
            ))}
          </div>
          <p className="sr-only" role="status">
            {visibleProjects.length}{" "}
            {visibleProjects.length === 1 ? "project" : "projects"} shown
          </p>
          <div className="project-grid">
            {visibleProjects.map(
              ({
                title,
                category,
                icon: Icon,
                description,
                url,
                label,
                tags,
                featured,
              }) => (
                <article
                  key={title}
                  className={`project-card${featured ? " featured-project" : ""}`}
                >
                  <div className="project-card-top">
                    <Icon size={32} aria-hidden="true" />
                    <span className="eyebrow">{category} / Experiment</span>
                  </div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                  <ul className="tag-list">
                    {tags.map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                  <a
                    className="project-card-link"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {label} <ArrowUpRight aria-hidden="true" />
                  </a>
                </article>
              ),
            )}
          </div>
        </div>
      </section>
    </>
  );
};
